import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  Pagination,
  Button,
  Tooltip,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Grid,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { alpha } from '@mui/material/styles';
import LoadingSpinner from '../components/LoadingSpinner';
import { keyframes } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const highlightAnimation = keyframes`
  0% {
    background-color: rgba(25, 118, 210, 0.1);
  }
  50% {
    background-color: rgba(25, 118, 210, 0.2);
  }
  100% {
    background-color: transparent;
  }
`;

const AllAdmins = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showBalances, setShowBalances] = useState({});
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const itemsPerPage = 10;
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundType, setFundType] = useState('credit');
  const [fundingUser, setFundingUser] = useState(null);
  const [isFunding, setIsFunding] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [makeAdminDialogOpen, setMakeAdminDialogOpen] = useState(false);
  const [selectedUserForAdmin, setSelectedUserForAdmin] = useState(null);
  const [adminRole, setAdminRole] = useState('admin');
  const [isMakingAdmin, setIsMakingAdmin] = useState(false);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedAdminForPermissions, setSelectedAdminForPermissions] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, adminsData] = await Promise.all([
        api.dashboard.getAllUsers(),
        api.dashboard.getAllAdmins()
      ]);
      
      // Sort users by creation date (most recent first)
      const sortedUsers = usersData?.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      ) || [];
      
      // Sort admins by creation date (most recent first)
      const sortedAdmins = adminsData?.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      ) || [];

      setUsers(sortedUsers);
      setAdmins(sortedAdmins);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Error fetching data');
      setUsers([]);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const filteredData = tabValue === 0 
    ? users.filter(
        (user) =>
          (user.name || user.fullname || '').toLowerCase().includes(filter.toLowerCase()) ||
          (user.email || '').toLowerCase().includes(filter.toLowerCase()) ||
          (user.username || '').toLowerCase().includes(filter.toLowerCase()) ||
          (user.phone || '').toLowerCase().includes(filter.toLowerCase())
      )
    : admins.filter(
        (admin) =>
          (admin.name || admin.fullname || '').toLowerCase().includes(filter.toLowerCase()) ||
          (admin.email || '').toLowerCase().includes(filter.toLowerCase()) ||
          (admin.username || '').toLowerCase().includes(filter.toLowerCase())
      );

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAvatarClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    // Log admin activity for viewing user details
    api.dashboard.logAdminActivity({
      action: 'VIEW_USER_DETAILS',
      details: `Viewed details for ${tabValue === 0 ? 'user' : 'admin'}: ${user.name || user.fullname}`,
      timestamp: new Date().toISOString(),
      adminId: localStorage.getItem('adminId'),
      adminName: localStorage.getItem('adminName')
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const maskBvn = (bvn) => {
    if (!bvn || typeof bvn !== 'string' || bvn.length < 6) return bvn || '-';
    return bvn.slice(0, 2) + '*****' + bvn.slice(-4);
  };

  const getStatusColor = (status) => '#22c55e';

  const getBalanceColor = (balance) => {
    if (typeof balance !== 'number') return '#111';
    if (balance > 10000) return '#22c55e'; // green
    if (balance >= 1000) return '#facc15'; // yellow
    return '#ef4444'; // red
  };

  const handleToggleBalance = (userId) => {
    setShowBalances((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [userId]: true }));
      
      if (tabValue === 0) {
        await api.dashboard.updateUserStatus(userId, newStatus);
      } else {
        await api.dashboard.updateAdminStatus(userId, newStatus);
      }

      // Log admin activity
      await api.dashboard.logAdminActivity({
        action: newStatus === 'active' ? 'ACTIVATE_ACCOUNT' : 'SUSPEND_ACCOUNT',
        details: `${tabValue === 0 ? 'User' : 'Admin'} ${newStatus === 'active' ? 'activated' : 'suspended'}`,
        timestamp: new Date().toISOString(),
        adminId: localStorage.getItem('adminId'),
        adminName: localStorage.getItem('adminName')
      });

      toast.success(`${tabValue === 0 ? 'User' : 'Admin'} ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      
      // Refresh data and highlight the updated item
      await fetchData();
      setHighlightedItem(userId);
      setTimeout(() => setHighlightedItem(null), 2000); // Remove highlight after 2 seconds
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRoleChange = async (adminId, newRole) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [adminId]: true }));
      await api.dashboard.updateAdminRole(adminId, newRole);

      // Log admin activity
      await api.dashboard.logAdminActivity({
        action: 'UPDATE_ADMIN_ROLE',
        details: `Updated admin role to ${newRole}`,
        timestamp: new Date().toISOString(),
        adminId: localStorage.getItem('adminId'),
        adminName: localStorage.getItem('adminName')
      });

      toast.success('Admin role updated successfully');
      
      // Switch to appropriate tab based on new role
      if (newRole === 'user') {
        setTabValue(0); // Switch to Users tab
      } else {
        setTabValue(1); // Stay on Admins tab
      }
      
      await fetchData();
      setHighlightedItem(adminId);
      setTimeout(() => setHighlightedItem(null), 2000);
    } catch (error) {
      console.error('Error updating admin role:', error);
      toast.error(error.message || 'Failed to update admin role');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [adminId]: false }));
    }
  };

  const handleOpenMakeAdminDialog = async (user) => {
    setSelectedUserForAdmin(user);
    setAdminRole('admin');
    setInitialPermissions([]);
    
    // Load available permissions if not already loaded
    if (availablePermissions.length === 0) {
      try {
        const response = await api.dashboard.getAllPermissions();
        setAvailablePermissions(response.data || []);
      } catch (error) {
        console.error('Error loading permissions:', error);
      }
    }
    
    setMakeAdminDialogOpen(true);
  };

  const handleMakeAdmin = async () => {
    try {
      setIsMakingAdmin(true);
      console.log('Making user admin with role:', adminRole);
      await api.dashboard.makeUserAdmin(selectedUserForAdmin._id, adminRole);

      // If permissions were selected, assign them
      if (initialPermissions.length > 0) {
        try {
          await api.dashboard.updateAdminPermissions(
            selectedUserForAdmin.username,
            initialPermissions
          );
        } catch (permError) {
          console.error('Error assigning permissions:', permError);
          toast.error('Admin created but failed to assign permissions. You can assign them manually.');
        }
      }

      // Log admin activity
      await api.dashboard.logAdminActivity({
        action: 'MAKE_USER_ADMIN',
        details: `Made user ${selectedUserForAdmin.fullname} (${selectedUserForAdmin.username}) an admin with role: ${adminRole}${initialPermissions.length > 0 ? ` and ${initialPermissions.length} permissions` : ''}`,
        timestamp: new Date().toISOString(),
        adminId: localStorage.getItem('adminId'),
        adminName: localStorage.getItem('adminName')
      });

      toast.success('User successfully made admin' + (initialPermissions.length > 0 ? ' with permissions assigned' : ''));
      setMakeAdminDialogOpen(false);
      setSelectedUserForAdmin(null);
      setInitialPermissions([]);
      
      // Switch to admins tab and refresh data
      setTabValue(1);
      await fetchData();
      setHighlightedItem(selectedUserForAdmin._id);
      setTimeout(() => setHighlightedItem(null), 2000);
    } catch (error) {
      console.error('Error making user admin:', error);
      toast.error(error.message || 'Failed to make user admin');
    } finally {
      setIsMakingAdmin(false);
    }
  };

  const handleOpenPermissionsDialog = async (admin) => {
    try {
      setIsLoadingPermissions(true);
      setSelectedAdminForPermissions(admin);
      
      // Fetch available permissions and current admin permissions
      const [allPermsResponse, adminPermsResponse] = await Promise.all([
        api.dashboard.getAllPermissions(),
        api.dashboard.getAdminPermissions(admin.username)
      ]);
      
      setAvailablePermissions(allPermsResponse.data || []);
      setSelectedPermissions(adminPermsResponse.data?.permissions || admin.permissions || []);
      setPermissionsDialogOpen(true);
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error(error.message || 'Failed to load permissions');
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const handlePermissionToggle = (permission) => {
    setSelectedPermissions(prev => 
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleSavePermissions = async () => {
    try {
      setIsSavingPermissions(true);
      await api.dashboard.updateAdminPermissions(
        selectedAdminForPermissions.username,
        selectedPermissions
      );

      // If updating current user's permissions, refresh their data
      if (currentUser && selectedAdminForPermissions.username === currentUser.username) {
        try {
          if (refreshUser) {
            await refreshUser();
            toast.success('Your permissions have been updated. The menu will refresh automatically.');
          } else {
            // Fallback: reload page
            setTimeout(() => window.location.reload(), 1000);
          }
        } catch (error) {
          console.error('Error refreshing current user data:', error);
          // Fallback: reload page
          setTimeout(() => window.location.reload(), 1000);
        }
      }

      toast.success('Permissions updated successfully');
      setPermissionsDialogOpen(false);
      setSelectedAdminForPermissions(null);
      await fetchData();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.message || 'Failed to save permissions');
    } finally {
      setIsSavingPermissions(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {tabValue === 0 ? 'All Users' : 'All Admins'}
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab 
          icon={<AccountBalanceWalletIcon />} 
          label="Users" 
          iconPosition="start"
        />
        <Tab 
          icon={<AdminPanelSettingsIcon />} 
          label="Admins" 
          iconPosition="start"
        />
      </Tabs>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                {tabValue === 1 && <TableCell>Role</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow 
                  key={item._id || item.id} 
                  hover
                  sx={{
                    animation: highlightedItem === (item._id || item.id) 
                      ? `${highlightAnimation} 2s ease-in-out` 
                      : 'none'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={item.profilePicture}
                        alt={item.name || item.fullname}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleAvatarClick(item)}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.name || item.fullname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>{item.phone || '-'}</TableCell>
                  {tabValue === 1 && (
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={item.role || 'admin'}
                          onChange={(e) => handleRoleChange(item._id || item.id, e.target.value)}
                          disabled={updatingStatus[item._id || item.id]}
                        >
                          <MenuItem value="superadmin">Super Admin</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="support">Support</MenuItem>
                          <MenuItem value="user">User</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={item.status || 'active'}
                      color={item.status === 'active' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getBalanceColor(item.balance),
                          fontWeight: 'bold',
                        }}
                      >
                        ₦{showBalances[item._id || item.id] ? item.balance?.toLocaleString() : '****'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleBalance(item._id || item.id)}
                      >
                        {showBalances[item._id || item.id] ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={item.status === 'active' ? 'Suspend' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(item._id || item.id, item.status === 'active' ? 'suspended' : 'active')}
                          disabled={updatingStatus[item._id || item.id]}
                        >
                          {item.status === 'active' ? (
                            <BlockIcon color="error" />
                          ) : (
                            <CheckCircleIcon color="success" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {tabValue === 0 && (
                        <Tooltip title="Make Admin">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenMakeAdminDialog(item)}
                          >
                            <PersonAddIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {tabValue === 1 && item.type !== 'superadmin' && (
                        <Tooltip title="Manage Permissions">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPermissionsDialog(item)}
                          >
                            <SecurityIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Paper>

      {/* User Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        {selectedUser && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {selectedUser.name || selectedUser.fullname}'s Details
                </Typography>
                <IconButton onClick={handleDialogClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{selectedUser.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{selectedUser.phone || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Username
                  </Typography>
                  <Typography variant="body1">{selectedUser.username}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Balance
                  </Typography>
                  <Typography variant="body1">₦{selectedUser.balance?.toLocaleString()}</Typography>
                </Grid>
                {tabValue === 1 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Role
                    </Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {selectedUser.role || 'admin'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedUser.status || 'active'}
                    color={selectedUser.status === 'active' ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Joined Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedUser.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Make Admin Dialog */}
      <Dialog
        open={makeAdminDialogOpen}
        onClose={() => {
          setMakeAdminDialogOpen(false);
          setSelectedUserForAdmin(null);
          setInitialPermissions([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Make User Admin
            </Typography>
            <IconButton 
              onClick={() => {
                setMakeAdminDialogOpen(false);
                setSelectedUserForAdmin(null);
                setInitialPermissions([]);
              }}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 3 }}>
            Make <strong>{selectedUserForAdmin?.fullname}</strong> ({selectedUserForAdmin?.username}) an admin and optionally assign permissions.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Admin Role</InputLabel>
            <Select
              value={adminRole}
              onChange={(e) => setAdminRole(e.target.value)}
              label="Admin Role"
            >
              <MenuItem value="superadmin">Super Admin</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="support">Support</MenuItem>
            </Select>
          </FormControl>

          {adminRole !== 'superadmin' && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Assign Permissions (Optional)
              </Typography>
              <Box sx={{ 
                maxHeight: 300, 
                overflowY: 'auto', 
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
                <FormGroup>
                  {availablePermissions.map((permission) => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Checkbox
                          checked={initialPermissions.includes(permission)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInitialPermissions([...initialPermissions, permission]);
                            } else {
                              setInitialPermissions(initialPermissions.filter(p => p !== permission));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      }
                    />
                  ))}
                </FormGroup>
                {availablePermissions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Loading permissions...
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {initialPermissions.length} permission{initialPermissions.length !== 1 ? 's' : ''} selected
              </Typography>
            </Box>
          )}
          
          {adminRole === 'superadmin' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Super Admins automatically have all permissions and cannot have permissions restricted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setMakeAdminDialogOpen(false);
              setSelectedUserForAdmin(null);
              setInitialPermissions([]);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMakeAdmin}
            variant="contained"
            color="primary"
            disabled={isMakingAdmin}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            {isMakingAdmin ? 'Making Admin...' : 'Make Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Management Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => {
          setPermissionsDialogOpen(false);
          setSelectedAdminForPermissions(null);
          setSelectedPermissions([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Manage Permissions
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                {selectedAdminForPermissions?.fullname || selectedAdminForPermissions?.username}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                setPermissionsDialogOpen(false);
                setSelectedAdminForPermissions(null);
                setSelectedPermissions([]);
              }}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  bgcolor: alpha('#fff', 0.1),
                  transform: 'rotate(90deg)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: theme.palette.background.default }}>
          {isLoadingPermissions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress size={50} />
            </Box>
          ) : (
            <Box>
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  <strong>Select permissions</strong> for this admin. Only selected permissions will be granted. 
                  Superadmins automatically have all permissions.
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={`${selectedPermissions.length} Selected`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={`${availablePermissions.length} Total`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {(() => {
                  // Organize permissions by category
                  const categories = {
                    'Financial Data & Earnings': availablePermissions.filter(p => p.includes('financial') || p.includes('earnings')),
                    'View Permissions': availablePermissions.filter(p => p.startsWith('view_') && !p.includes('financial') && !p.includes('earnings')),
                    'Withdrawal Actions': availablePermissions.filter(p => p.includes('withdrawal')),
                    'KYC Actions': availablePermissions.filter(p => p.includes('kyc')),
                    'Event Actions': availablePermissions.filter(p => p.includes('event')),
                    'Vendor Actions': availablePermissions.filter(p => p.includes('vendor')),
                    'ATM Card Actions': availablePermissions.filter(p => p.includes('atm_card')),
                    'Savings Actions': availablePermissions.filter(p => p.includes('saving')),
                    'Affiliate Actions': availablePermissions.filter(p => p.includes('affiliate')),
                    'User Management': availablePermissions.filter(p => (p.includes('user') || p.includes('suspend') || p.includes('activate') || p.includes('fund') || p.includes('debit')) && !p.includes('view_users')),
                    'Notification Actions': availablePermissions.filter(p => p.includes('notification') && !p.includes('view_notifications')),
                    'Advert Actions': availablePermissions.filter(p => p.includes('advert')),
                    'Admin Management': availablePermissions.filter(p => p.includes('admin') && !p.includes('view_admins')),
                    'Settings & Data': availablePermissions.filter(p => (p.includes('settings') || p.includes('export') || p.includes('dashboard')) && !p.includes('financial') && !p.includes('earnings'))
                  };

                  return Object.entries(categories).map(([category, permissions]) => {
                    if (permissions.length === 0) return null;
                    
                    const categorySelectedCount = permissions.filter(p => selectedPermissions.includes(p)).length;
                    const categoryTotalCount = permissions.length;
                    
                    return (
                      <Paper
                        key={category}
                        elevation={0}
                        sx={{
                          mb: 2.5,
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              fontSize: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            {category}
                          </Typography>
                          <Chip
                            label={`${categorySelectedCount}/${categoryTotalCount}`}
                            size="small"
                            color={categorySelectedCount === categoryTotalCount ? 'success' : categorySelectedCount > 0 ? 'primary' : 'default'}
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        </Box>
                        <Grid container spacing={1.5}>
                          {permissions.map((permission) => {
                            const isSelected = selectedPermissions.includes(permission);
                            return (
                              <Grid item xs={12} sm={6} md={4} key={permission}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: `1.5px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.2)}`,
                                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      borderColor: theme.palette.primary.main,
                                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    }
                                  }}
                                  onClick={() => handlePermissionToggle(permission)}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handlePermissionToggle(permission);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        size="small"
                                        sx={{
                                          color: theme.palette.primary.main,
                                          '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                          }
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontSize: '0.875rem',
                                          fontWeight: isSelected ? 600 : 400,
                                          color: isSelected ? theme.palette.primary.main : 'text.primary',
                                          transition: 'all 0.2s ease',
                                          pointerEvents: 'none'
                                        }}
                                      >
                                        {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </Typography>
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePermissionToggle(permission);
                                    }}
                                    sx={{ 
                                      mb: 0,
                                      width: '100%',
                                      m: 0,
                                      cursor: 'pointer',
                                      '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    );
                  });
                })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: theme.palette.background.default, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Button
            onClick={() => {
              setPermissionsDialogOpen(false);
              setSelectedAdminForPermissions(null);
              setSelectedPermissions([]);
            }}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            color="primary"
            disabled={isSavingPermissions || isLoadingPermissions}
            sx={{
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: theme.palette.grey[300],
                boxShadow: 'none',
              }
            }}
            startIcon={isSavingPermissions ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSavingPermissions ? 'Saving...' : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllAdmins; 